
import * as React from 'react';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Typography from '@mui/material/Typography';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import BasicInfo from './basicInfo';
import Insurance from './insurance';
import Physician from './physician';
import MedicalHistory from './medicalHistory';
import LifeStyleInfo from './lifeStyle';
import Acknowledgement from './acknowledgement';
import { getMasterDataRequest } from '../../../../redux/modules/admin/patientProfile/slice';
import { resetPostPatientAcknowledgement, resetPutPatientAcknowledgement } from '../../../../redux/modules/admin/patientAcknowledgement/slice';
import { resetPostPatientInformation, resetPut } from '../../../../redux/modules/admin/patientInformation/slice';
import { resetPostPatientInsurance, resetPutPatientInsurance } from '../../../../redux/modules/admin/patientInsurance/slice';
import { resetPostPatientPhysician, resetPutPatientPhysician } from '../../../../redux/modules/admin/patientPhysician/slice';
import { resetPostPatientMedicalHistory, resetPutMedicalHistory } from '../../../../redux/modules/admin/patientMedicalHistory/slice';
import { resetPostPatientLifeStyle, resetPutPatientLifeStyle } from '../../../../redux/modules/admin/patientLifeStyle/slice';
import { toast } from 'react-toastify';
const steps = [
    'Basic Information',
    'Insurance',
    'Physician',
    'Medical History',
    'LifeStyle',
    'Consents & Acknowledgements',
];

export default function FormStepper({ itemData, toggleList, }) {
    const dispatch = useDispatch();
    const [activeStep, setActiveStep] = useState(0);
    const [showSuccess, setShowSuccess] = useState(false);
    const [formData, setFormData] = useState({
        basicInfo: {},
        insurance: {},
        physician: {},
        medicalHistory: {},
        lifeStyleInfo: {},
        acknowledgement: {},
    });
    
    let { putRes, postRes } = useSelector(state => state.patientInformation);
    let { putRes: putInsurance, postRes: postInsurance } = useSelector(state => state.patientInsurance);
    let { putRes: putPhysician, postRes: postPhysician } = useSelector(state => state.patientPhysician);
    let { putRes: putMedicalHistory, postRes: postMedicalHistory } = useSelector(state => state.patientMedicalHistory);
    let { putRes: putLifeStyle, postRes: postLifeStyle } = useSelector(state => state.patientLifeStyle);
    let { putRes: putAcknowledgement, postRes: postAcknowledgement } = useSelector(state => state.patientAcknowledgement);

    useEffect(() => {
        dispatch(getMasterDataRequest());
        
    }, [dispatch, putRes, postRes, toggleList]);


    useEffect(() => {
        // Handle one step's response: surface success OR failure, then reset
        // so a stale failure response doesn't keep firing toasts. Previously
        // only success was handled, which let duplicate-email / duplicate
        // name-and-DOB 409s pass silently — the user kept clicking through
        // until acknowledgement (also failing), saw the "All steps completed"
        // text, and never realised no patient was ever created.
        const handleStepResponse = (res, resetAction, opts = {}) => {
            if (!res) return;
            if (res.success) {
                toast.success(res.message);
                if (opts.onSuccess) opts.onSuccess(res);
            } else {
                const msg = res?.message || res?.body?.message
                    || (typeof res === 'string' ? res : 'Save failed. Please try again.');
                toast.error(msg);
            }
            dispatch(resetAction());
        };

        handleStepResponse(postRes, resetPostPatientInformation, {
            onSuccess: (r) => {
                if (r?.pId) localStorage.setItem("pId", r.pId);
                // Advance to step 2 only now that the patient is confirmed
                // created. basicInfo.onSubmit deliberately skips advancing
                // for the POST path so this is the single source of truth.
                setActiveStep((prev) => (prev === 0 ? 1 : prev));
            },
        });
        handleStepResponse(putRes, resetPut);
        handleStepResponse(postInsurance, resetPostPatientInsurance);
        handleStepResponse(putInsurance, resetPutPatientInsurance);
        handleStepResponse(postPhysician, resetPostPatientPhysician);
        handleStepResponse(putPhysician, resetPutPatientPhysician);
        handleStepResponse(postMedicalHistory, resetPostPatientMedicalHistory);
        handleStepResponse(putMedicalHistory, resetPutMedicalHistory);
        handleStepResponse(postLifeStyle, resetPostPatientLifeStyle);
        handleStepResponse(putLifeStyle, resetPutPatientLifeStyle);

        const finishOnSuccess = () => {
            setShowSuccess(true);
            setTimeout(() => toggleList(), 1500);
        };
        handleStepResponse(postAcknowledgement, resetPostPatientAcknowledgement, { onSuccess: finishOnSuccess });
        handleStepResponse(putAcknowledgement, resetPutPatientAcknowledgement, { onSuccess: finishOnSuccess });
    }, [dispatch, toggleList, postRes, putRes, postInsurance, putInsurance, postPhysician, putPhysician, postMedicalHistory, putMedicalHistory, postLifeStyle, putLifeStyle, postAcknowledgement, putAcknowledgement])

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    

    const handleStepChange = (stepData) => {
        setFormData({ ...formData, ...stepData });
        handleNext();
    };
    if (showSuccess) {
        return (
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 6 }}>
                <Paper
                    elevation={0}
                    sx={{
                        px: 5,
                        py: 4,
                        borderRadius: 3,
                        bgcolor: 'rgba(46,125,50,0.08)',
                        border: '1px solid rgba(46,125,50,0.25)',
                        textAlign: 'center',
                        maxWidth: 480,
                    }}
                >
                    <CheckCircleRoundedIcon sx={{ fontSize: 56, color: 'success.main', mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.dark', mb: 0.5 }}>
                        Patient record saved
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Returning to the patient list…
                    </Typography>
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Stepper activeStep={activeStep} nonLinear>
                {steps.map((label, index) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>
            <div>
                {activeStep === 0 && (
                    <BasicInfo onNext={handleStepChange} formData={formData.basicInfo} itemData={itemData} toggleList={toggleList} />
                )}
                {activeStep === 1 && (
                    <Insurance onNext={handleStepChange} formData={formData.insurance} onBack={handleBack} patientId={itemData?._id} itemData={itemData?.patientInsurance?.[0]} toggleList={toggleList} />
                )}
                {activeStep === 2 && (
                    <Physician onNext={handleStepChange} formData={formData.physician} onBack={handleBack} patientId={itemData?._id} itemData={itemData?.patientPhysician?.[0]} toggleList={toggleList} />
                )}
                {activeStep === 3 && (
                    <MedicalHistory onNext={handleStepChange} formData={formData.medicalHistory} onBack={handleBack} patientId={itemData?._id} itemDataId={itemData?._id} itemData={itemData?.patientMedicalHistory?.[0]} toggleList={toggleList} />
                )}
                {activeStep === 4 && (
                    <LifeStyleInfo onNext={handleStepChange} formData={formData.lifeStyleInfo} onBack={handleBack} patientId={itemData?._id} itemData={itemData?.patientLifeStyleInfo?.[0]} toggleList={toggleList} />
                )}
                {activeStep === 5 && (
                    <Acknowledgement onNext={handleStepChange} formData={formData.acknowledgement} onBack={handleBack} patientId={itemData?._id} itemData={itemData?.patientAcknowledgement?.[0]} toggleList={toggleList} />
                )}
            </div>
        </Box>
    );
}
